/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Add, Cancel, Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Button from "@jumbo/components/JumboLoadingButton";
import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import moment from "moment";
import { SAMPLES } from "app/routes/constant";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import Loader from "@jumbo/components/Loader";
import { getProductList } from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";

import { getVendorList } from "app/redux/slice/vendorSlice";
import {
  createSample,
  editSample,
  updateSample,
} from "app/redux/slice/sampleSlice";

const useStyles = makeStyles({
  headingTitle: {
    minWidth: "80px",
  },
  table: {
    minWidth: 650,
    boxShadow: "none",
    "& .MuiTableCell-root": {
      boxShadow: "none",
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "300px",
  },
  variants: {
    minWidth: "300px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "6px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
  imageUploadContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "5px",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    minHeight: "120px",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#1976d2",
    },
  },
  imagePreview: {
    width: "80px",
    height: "80px",
    objectFit: "cover",
    borderRadius: "8px",
  },
  hiddenInput: {
    display: "none",
  },
});

const AddEditSample = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const vendorData = useSelector((state) => state?.vendor);
  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);
  const mobileView = useMediaQuery("(max-width:575px)");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState();

  const validationSchema = yup.object({
    vendorId: yup.string().required("Please select a vendor").nullable(),
    takenDate: yup.string().required("Please enter an taken date."),
    items: yup.array().of(
      yup.object().shape({
        productId: yup.string(),
        variantId: yup.string(),
        quantity: yup
          .number()
          .typeError("Quantity must be a number")
          .required("Please enter a quantity")
          .min(1, "Must be greater than or equal to 1"),
      })
    ),
  });

  const removeBlankStringKeys = (obj) =>
    Array.isArray(obj)
      ? obj.map(removeBlankStringKeys)
      : obj && typeof obj === "object"
      ? Object.fromEntries(
          Object.entries(obj)
            .filter(([_, v]) => v !== "")
            .map(([k, v]) => [k, removeBlankStringKeys(v)])
        )
      : obj;

  const formik = useFormik({
    initialValues: {
      vendorId: "",
      takenDate: moment(new Date()).format("YYYY-MM-DD"),
      items: [
        {
          variantId: "",
          productId: "",
          quantity: "",
        },
      ],
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validate: (values) => {
      const errors = {};

      values.items.forEach((item, index) => {
        if (!item.productId) {
          if (!errors.items) errors.items = [];
          if (!errors.items[index]) errors.items[index] = {};
          errors.items[index].productId = "Please select a product";
        }

        const productVariants =
          variantData?.list?.filter(
            (data) => data?.productId === item?.productId
          ) ?? [];

        if (productVariants.length > 0 && !item.variantId) {
          if (!errors.items) errors.items = [];
          if (!errors.items[index]) errors.items[index] = {};
          errors.items[index].variantId = "Please select a variant";
        }
      });

      return errors;
    },
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      const newItems = removeBlankStringKeys(values?.items);
      const finalValues = { ...values, items: newItems };

      try {
        const response = await dispatch(
          id
            ? updateSample({ id, data: finalValues })
            : createSample(finalValues)
        ).unwrap();

        if (response?.status === 200) {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "success",
          });
          navigate(SAMPLES);
          resetForm();
        } else {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar(error?.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors } = formik;
  const { isSubmitting } = formik;

  const handleChange = (index, field, value) => {
    let updatedFields = [...values.items];
    updatedFields[index][field] = value;

    if (field === "productId") {
      updatedFields[index].variantId = "";
    }

    setFieldValue("items", updatedFields);

    if (field === "productId") {
      setFieldValue(`items[${index}].variantId`, "");
    }
  };

  const handleAddMore = () => {
    const newItems = [
      ...formik.values.items,
      {
        variantId: "",
        productId: "",
        quantity: "",
      },
    ];
    formik.setValues({
      ...formik.values,
      items: newItems,
    });
  };

  const handleDeleteField = (index) => {
    let updatedFields = [...values?.items];
    updatedFields.splice(index, 1);
    setFieldValue("items", updatedFields);
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(editSample(id))
        .unwrap()
        .then((response) => {
          if (response?.status === 200) {
            const sampleDetail = response?.data?.data;
            setFieldValue(
              "takenDate",
              moment(sampleDetail?.takenDate).format("YYYY-MM-DD")
            );
            setFieldValue("vendorId", sampleDetail?.vendorId);
            setStatus(sampleDetail?.status);
            let productArr = [];
            for (let i = 0; i < sampleDetail?.items.length; i++) {
              const element = sampleDetail?.items[i];
              productArr?.push({
                variantId: element?.variantId?._id,
                productId: element?.productId?._id,
                quantity: element?.quantity,
              });
            }
            setFieldValue("items", productArr);

            setLoading(false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
            setLoading(false);
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    dispatch(getProductList({ sort: 1, field: "name" }));
    dispatch(getVariantList());
    dispatch(getVendorList({ sort: 1, field: "name" }));
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <PageContainer heading={id ? "Update Sample" : "Add Sample"}>
          <Card>
            <CardContent>
              <FormikProvider value={formik}>
                <Form
                  noValidate
                  autoComplete="off"
                  onSubmit={formik.handleSubmit}
                >
                  <Grid container border={1}>
                    <Grid sm={6} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1}>
                      <JumboAutoComplete
                        options={vendorData?.list ?? []}
                        label="Vendor*"
                        size="small"
                        getOptionLabel={(option) => option?.name}
                        name={"vendorId"}
                        value={vendorData?.list?.find(
                          (item) => item?._id === values?.vendorId
                        )}
                        onChange={(evt, value) => {
                          setFieldValue("vendorId", value?._id);
                        }}
                        helperText={formik?.errors?.vendorId}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                      />
                    </Grid>
                    <Grid item sm={6} xs={12} p={1}>
                      <TextField
                        label={"Taken Date*"}
                        name="takenDate"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(evt) =>
                          setFieldValue("takenDate", evt.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values?.takenDate}
                        error={errors?.takenDate}
                        helperText={errors?.takenDate}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    borderRight={1}
                    sx={{
                      mt: { xs: 3, md: 0 },
                    }}
                    className="mobile-view-table"
                  >
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 650 }}
                        size="small"
                        aria-label="a dense table"
                        className={`${classes?.table}`}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Sr. No.</TableCell>
                            <TableCell style={{ minWidth: "200px" }}>
                              Product
                            </TableCell>
                            <TableCell style={{ minWidth: "200px" }}>
                              Variant
                            </TableCell>
                            <TableCell style={{ minWidth: "120px" }}>
                              Quantity
                            </TableCell>

                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {values?.items?.length &&
                            values?.items?.map((item, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes?.alignTop}
                                    sx={{ width: "10px", paddingTop: "20px" }}
                                  >
                                    {index + 1}
                                  </TableCell>

                                  <TableCell className={`${classes?.alignTop}`}>
                                    <JumboAutoComplete
                                      sx={{ marginTop: "7px" }}
                                      options={productData?.list ?? []}
                                      label="Product*"
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`items[${index}].productId`}
                                      value={productData?.list?.find(
                                        (data) => data?._id === item?.productId
                                      )}
                                      onChange={(evt, value) => {
                                        handleChange(
                                          index,
                                          "productId",
                                          value?._id
                                        );
                                      }}
                                      helperText={
                                        formik?.errors?.items?.length &&
                                        formik?.errors?.items[index]?.productId
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className={`${classes?.alignTop}`}>
                                    <JumboAutoComplete
                                      sx={{ marginTop: "7px" }}
                                      options={
                                        variantData?.list?.filter(
                                          (data) =>
                                            data?.productId === item?.productId
                                        ) ?? []
                                      }
                                      label={
                                        variantData?.list?.filter(
                                          (data) =>
                                            data?.productId === item?.productId
                                        )?.length > 0
                                          ? "Variant*"
                                          : "Variant"
                                      }
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`items[${index}].variantId`}
                                      value={
                                        item?.variantId
                                          ? variantData?.list?.find(
                                              (data) =>
                                                data?._id === item?.variantId
                                            )
                                          : null
                                      }
                                      onChange={(evt, value) => {
                                        handleChange(
                                          index,
                                          "variantId",
                                          value?._id
                                        );
                                      }}
                                      helperText={
                                        formik?.errors?.items?.length &&
                                        formik?.errors?.items[index]?.variantId
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className={`${classes?.alignTop}`}>
                                    <TextField
                                      sx={{ marginTop: "7px" }}
                                      placeholder="Qty"
                                      label="Qty*"
                                      name={`items[${index}].quantity`}
                                      fullWidth
                                      value={item?.quantity}
                                      size="small"
                                      type="number"
                                      onChange={(evt) =>
                                        handleChange(
                                          index,
                                          "quantity",
                                          +evt.target.value
                                        )
                                      }
                                      error={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.quantity
                                      }
                                      helperText={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.quantity
                                      }
                                    />
                                  </TableCell>

                                  <TableCell sx={{ width: "10px" }}>
                                    {values?.items?.length > 1 && (
                                      <IconButton
                                        aria-label="delete"
                                        color="secondary"
                                        onClick={() => handleDeleteField(index)}
                                      >
                                        <Cancel />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Grid className={classes?.buttonContainer}>
                                <Button
                                  style={{ minWidth: "100px" }}
                                  type="button"
                                  buttonLabel={"Add"}
                                  startIcon={<Add />}
                                  variant="outlined"
                                  onClick={handleAddMore}
                                  className="add-btn"
                                />
                              </Grid>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid
                      item
                      sm={12}
                      xs={12}
                      className={classes?.loadingButtonContainer}
                    >
                      {status === 1 && (
                        <LoadingButton
                          type="submit"
                          buttonLabel={"Update"}
                          sx={{ marginRight: "10px" }}
                          startIcon={<Save />}
                          variant="outlined"
                          fullWidth={mobileView}
                          loading={isSubmitting}
                        />
                      )}
                      {!id && (
                        <LoadingButton
                          type="submit"
                          buttonLabel={"Save"}
                          sx={{ marginRight: "10px" }}
                          startIcon={<Save />}
                          variant="outlined"
                          fullWidth={mobileView}
                          loading={isSubmitting}
                        />
                      )}

                      <LoadingButton
                        color={"secondary"}
                        buttonLabel={"Close"}
                        variant="outlined"
                        startIcon={<Close />}
                        onClick={() => navigate(SAMPLES)}
                        fullWidth={mobileView}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </FormikProvider>
            </CardContent>
          </Card>
        </PageContainer>
      )}
    </>
  );
};

export default AddEditSample;
